import { I18N_NS } from "Config/i18n";
import { ScrollableTable, TableColumnProps } from "Components/ScrollableTable/ScrollableTable";
import { useTranslation } from "react-i18next";
import { useEffect, useState } from "react";
import { Button } from "Components/General/Button";
import { WithModal, withModal } from "Components/General/Modal/withModal";
import { Row } from "antd";
import { bcfIssuesStore } from "../Stores/BCFIssuesStore";
// eslint-disable-next-line max-len
import { SharedAttachmentsTableItemActions } from "Pages/Project/Documents/SharedAttachments/SharedAttachmentsTableItemActions";
import { FormatData } from "utils/FormatData";
import { AttachProjectFiles } from "Pages/Project/Documents/SharedAttachments/AttachProjectFiles";
import { SharedAttachmentsTableActions } from "Pages/Project/Documents/SharedAttachments/SharedAttachmentsTableActions";
import { documentsTableStore } from "Pages/Project/Documents/Stores/DocumentsTableStore";

interface BCFAttachmentsTableProps extends WithModal {
  topicGuid: string;
  projectActions: { [action in API_BCFSERVERPROJECTEXTENSIONS_PROJECT_ACTIONS]?: boolean };
  topicDeleted?: boolean;
  isInImportMode?: boolean;
  conflictAttachments?: API_FileAttachment[];
  hideModal?: () => void;
}

export const BCFAttachmentsTable = withModal((props: BCFAttachmentsTableProps) => {
  const { t } = useTranslation(I18N_NS.PROJECT);
  const [selectedRowKeys, setSelectedRowKeys] = useState<number[]>([]);
  const [attachments, setAttachments] = useState<API_FileAttachment[]>([]);
  const [loading, setLoading] = useState<boolean>(false);

  useEffect(() => {
    if (props.isInImportMode) {
      if (props.conflictAttachments?.length) {
        setAttachments(props.conflictAttachments);
      }
    } else {
      getAttachments();
    }
  }, []);

  const getAttachments = async () => {
    try {
      setLoading(true);
      const response: API_FileAttachment[] = await bcfIssuesStore.fetchTopicAttachments(props.topicGuid);
      setAttachments(response);
    } catch (error) {
      throw error;
    } finally {
      setLoading(false);
    }
  };
  const handleSelectRows = ({ selectedRowKeys }) => {
    setSelectedRowKeys(selectedRowKeys);
  };

  const renderFileType = ({ rowData: file }: { rowData: API_FileAttachment }) => {
    // consider that if file was added from project - it would always have "parentId" prop
    if (!(file as API_FileDataEntity).parentId) {
      return file.type;
    } else {
      // consider that extension is in the last part after dot
      const splittedFileName = file.name.split(".");
      if (splittedFileName.length > 1) {
        // if filename has extension - length would be > 1
        return splittedFileName[splittedFileName.length - 1];
      } else {
        return "";
      }
    }
  };

  const columns = () => {
    const items: TableColumnProps<API_FileAttachment>[] = [
      {
        dataKey: "name",
        label: t("Filename"),
        flexGrow: 1,
        width: 140,
        // cellRenderer: ({ rowData }) => <FilenameCell file={rowData} />,
      },
      {
        dataKey: "type",
        label: t("Type"),
        width: 70,
        cellRenderer: renderFileType,
      },
      {
        dataKey: "size",
        label: t("Size"),
        width: 80,
        cellRenderer: ({ cellData }) => (Number.isNaN(cellData) ? null : FormatData.formatBytes(cellData, 1)),
      },
    ];
    if (!props.isInImportMode) {
      items.push({
        dataKey: "id",
        label: t("Actions"),
        disableSort: true,
        width: 80,
        cellRenderer: ({ rowData }) => (
          <SharedAttachmentsTableItemActions
            onRemove={remove}
            file={rowData}
            disabled={props.isInImportMode || props.topicDeleted}
            hideModal={props.hideModal}
          />
        ),
      });
    }
    return items;
  };

  const addFiles = async (files: API_FileAttachment[]) => {
    try {
      const fileIds = files.map((file) => file.id);
      await bcfIssuesStore.addFiles(props.topicGuid, fileIds);
      await getAttachments();
    } catch (error) {
      throw error;
    }
  };

  const showAddFile = () => {
    props.showModal(AttachProjectFiles, {
      modalProps: {
        title: t("Attach files from project"),
        width: 800,
        footer: null,
        zIndex: 999999 /*High value for BIM Viewer */,
      },
      componentProps: { addFiles: addFiles },
    });
  };

  const downloadSelectedFiles = () => {
    if (!selectedRowKeys.length) {
      return;
    }
    const selectedFiles = attachments.filter((obj) => selectedRowKeys.includes(obj.id));
    documentsTableStore.downloadFiles(selectedFiles);
  };
  const selectedFilesCanNotBeDownloaded = () => {
    const filteredFiles = attachments.filter((obj) => selectedRowKeys.includes(obj.id));
    return filteredFiles.some((file) => !file.fileActions || !file.fileActions.download);
  };

  const remove = async (fileId?: number) => {
    if (fileId) {
      await bcfIssuesStore.removeFiles(props.topicGuid, [fileId]);
    } else {
      await bcfIssuesStore.removeFiles(props.topicGuid, selectedRowKeys);
    }
    await getAttachments();
  };

  const tableActions = {
    files: attachments?.filter((obj) => selectedRowKeys.includes(obj.id)),
    onDownload: () => downloadSelectedFiles(),
    disabledDownload: props.isInImportMode || selectedFilesCanNotBeDownloaded() || props.topicDeleted,
    onRemoveSelectedFiles: () => remove(),
    disabledRemove: !props.projectActions.createDocument || props.topicDeleted,
  };

  return (
    <>
      <Row justify={props.isInImportMode ? "end" : "space-between"} style={{ marginBottom: 5 }}>
        {props.isInImportMode ? null : <SharedAttachmentsTableActions {...tableActions} />}
        {!props.isInImportMode && props.projectActions.createDocument && (
          <Button
            type="primary"
            faIcon="plus"
            onClick={showAddFile}
            disabled={props.isInImportMode || props.topicDeleted}
          >
            {t("New project file")}
          </Button>
        )}
      </Row>
      <ScrollableTable
        rowKey="id"
        wrapperStyles={{ height: null, flex: "1 1 auto", minHeight: 270 }}
        data={attachments}
        columns={columns()}
        onSelectRows={handleSelectRows}
        selectedRowKeys={selectedRowKeys}
        loading={loading}
      />
    </>
  );
});
